import { ColDef, type ICellRendererParams } from "ag-grid-community";
import { Typography } from "@mui/material";
import numeral from "numeral";
import { Fragment } from "react";
import { jiraIssuesColumnDef } from "../common/jiraIssuesColumnDef";
import { tagsColumnDef } from "../common/tagsColumnDef";
import { smartTagsColumnDef } from "../common/smartTagsColumnDef";
import { regionColumnDef } from "../common/regionColumnDef";
import { resourceCreatedByColumnDef } from "../common/resourceCreatedByColumnDef";
import { resourceTotalSpendColumnDef } from "../common/resourceTotalSpendColumnDef";
import { getResourceCreationTimeColumnDef } from "../common/getResourceCreationTimeColumnDef";
import { forecastedEOMColumnDef } from "../common/forecastedEOMColumnDef";
import { resourceAccountColumnDef } from "../common/resourceAccountColumnDef";
import { convertByteToSize } from "@/components/utils/helpers/convertByteToSize";
import { FilteredDaxClusterResourceItem } from "@/services/cloudchipr.api";
import { getResourceIdentifierColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceIdentifierColumnDef";
import { taskColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/taskColumnDef";
import { billingCostColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/billingCostColumnDef";
import { getListingPriceColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { getResourceMetricsColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceMetricsColumnDef";

export const daxClusterColumnDefs: ColDef[] = [
  getResourceIdentifierColumnDef("Cluster Name"),

  forecastedEOMColumnDef,
  billingCostColumnDef,
  getListingPriceColumnDef("dax_cluster"),

  resourceAccountColumnDef,

  getResourceCreationTimeColumnDef("Date Created"),

  resourceTotalSpendColumnDef,

  {
    field: "resource.node_type",
    headerName: "Node type",
  },

  {
    field: "resource.node_count",
    headerName: "Nodes",
  },

  getResourceMetricsColumnDef("CPU Max", "cpu_max", "dax_cluster"),

  getResourceMetricsColumnDef(
    "Memory Max",
    "mem_used_percent_max",
    "dax_cluster",
  ),

  resourceCreatedByColumnDef,

  {
    field: "resource.total_request_count_value",
    headerName: "Total Requests",
    cellRendererParams: { noData: 0 },
  },

  {
    field: "resource.cpu_reservation",
    headerName: "CPU Requests",
    headerTooltip: "Current CPU reservation.",
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDaxClusterResourceItem>) => {
      const value = data?.resource?.cpu_reservation;

      if (!value) {
        return "N/A";
      }

      return <Typography variant="body2">{value} vCPU</Typography>;
    },
  },

  {
    field: "resource.memory_reservation",
    headerName: "Memory Requests",
    headerTooltip: "Current Memory reservation.",
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDaxClusterResourceItem>) => {
      const value = data?.resource?.memory_reservation;

      if (!value) {
        return "N/A";
      }

      return (
        <Typography variant="body2">
          {numeral(value).format("0.0")} GB
        </Typography>
      );
    },
  },

  {
    field: "resource.network_bytes_in_value",
    headerName: "Network In/Out",
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDaxClusterResourceItem>) => {
      const networkIn = data?.resource?.network_bytes_in_value ?? null;
      const networkOut = data?.resource?.network_bytes_out_value ?? null;

      if (networkOut === null && networkIn === null) {
        return <Fragment>N/A</Fragment>;
      }

      return (
        <Fragment>
          {networkIn === null ? "-" : convertByteToSize(networkIn)}/
          {networkOut === null ? "-" : convertByteToSize(networkOut)}
        </Fragment>
      );
    },
  },

  regionColumnDef,
  tagsColumnDef,
  smartTagsColumnDef,
  taskColumnDef,
  jiraIssuesColumnDef,
];
